import Box, { type BoxProps } from '@mui/material/Box';
import { type SxProps, type Theme } from '@mui/material/styles';
import useTheme from '@mui/material/styles/useTheme';
import React, { type MouseEventHandler } from 'react';
import ReactPannellum from 'react-pannellum';
import { checkUndefinedNullOrEmpty, checkUndefinedOrNull } from '../_tools/Tools';

export type ImageProps = {
    align?: 'left' | 'center' | 'right' | 'justify';
    cover?: boolean;
    config?: any;
    height?: string;
    id: string;
    disablePlaceholder?: boolean;
    onClick?: MouseEventHandler;
    onMouseDown?: MouseEventHandler;
    onMouseUp?: MouseEventHandler;
    ratio?: [number, number];
    src: string;
    sx?: SxProps<Theme>;
} & BoxProps;

function Image360iElement(props: ImageProps) {
    const { config, id, align = 'left', cover, height, disablePlaceholder, onClick, onMouseDown, onMouseUp, ratio, src, sx } = props;
    const theme = useTheme();

    if (!checkUndefinedNullOrEmpty(ratio)) {
        return (
            <Box
                onClick={onClick}
                onMouseDown={onMouseDown}
                onMouseUp={onMouseUp}
                component="span"
                sx={{
                    'width': 1,
                    'lineHeight': 0,
                    'display': 'block',
                    'overflow': 'hidden',
                    'position': 'relative',
                    'background': disablePlaceholder ? 'transparent' : theme.palette.background.default,
                    'pt': height || 'calc(100% / ' + ratio[0] + ' * ' + ratio[1] + ')',
                    '& .wrapper': {
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        lineHeight: 0,
                        position: 'absolute',
                        backgroundSize: 'cover !important',
                    },
                    '& .wrapper.lazy-load-image-background.blur.lazy-load-image-loaded.down': {
                        paddingTop: 0, // Bilder haben sich bei Müller(54100) verschoben
                    },
                    'cursor': !checkUndefinedOrNull(onClick) || (!checkUndefinedOrNull(onMouseDown) && !checkUndefinedOrNull(onMouseUp)) ? 'pointer' : undefined,
                    ...sx,
                }}
            >
                <Box
                    component={ReactPannellum}
                    sceneId={'ReactPannelium_' + id + '_firstScene'}
                    id={'ReactPannelium_' + id}
                    className="wrapper"
                    imageSource={src}
                    config={{ autoLoad: true, autoRotate: -2, ...config }}
                    sx={{
                        background: 'transparent',
                        width: cover && height ? 'calc(100% / (100% / ' + ratio[0] + ' * ' + ratio[1] + ') / ' + ratio[1] + ')' : '100% !important',
                        height: height || '100% !important',
                        objectFit: cover ? 'cover' : 'contain',
                        objectPosition: !cover ? (align === 'justify' ? 'center' : align) : undefined,
                        verticalAlign: 'center',
                    }}
                />
            </Box>
        );
    }

    return (
        <Box
            onClick={onClick}
            onMouseDown={onMouseDown}
            onMouseUp={onMouseUp}
            component="span"
            sx={{
                'lineHeight': 0,
                'display': 'block',
                'overflow': 'hidden',
                'background': disablePlaceholder ? 'transparent' : theme.palette.background.default,
                '& .wrapper': {
                    width: 1,
                    height: 1,
                    backgroundSize: 'cover !important',
                },
                'cursor': !checkUndefinedOrNull(onClick) || (!checkUndefinedOrNull(onMouseDown) && !checkUndefinedOrNull(onMouseUp)) ? 'pointer' : undefined,
                ...sx,
            }}
        >
            <Box
                component={ReactPannellum}
                sceneId={'ReactPannelium_' + id + '_firstScene'}
                id={'ReactPannelium_' + id}
                imageSource={src}
                className="wrapper"
                config={{ autoLoad: true, autoRotate: -2, ...config }}
                sx={{
                    background: 'transparent',
                    width: '100% !important',
                    height: height || '100% !important',
                    objectFit: cover ? 'cover' : 'contain',
                    objectPosition: !cover ? (align === 'justify' ? 'center' : align) : undefined,
                    verticalAlign: 'center',
                }}
            />
        </Box>
    );
}

export default React.memo(Image360iElement);
